import { createSlice } from "@reduxjs/toolkit";
import { send } from "src/utils/fetch";
import { captureException } from "src/utils/error";
import { createAppAsyncThunk } from "../async-thunk";

export interface MedEntitlements {
  hasTelehealthAddOn?: boolean;
  hasGlp1CoursePack?: boolean;
  hasMedCompanion?: boolean;
}

/** Tracks the lifecycle of `loadMedEntitlements` so consumers can distinguish
 *  "user has no entitlement" from "we don't know yet / fetch failed". Without
 *  this, a failed fetch leaves `hasMedCompanion === undefined` and silently
 *  downgrades Companion users to the wrong variant. */
export type MedEntitlementsLoadStatus =
  | "idle"
  | "loading"
  | "success"
  | "failure";

export type MedEntitlementsState = MedEntitlements & {
  loadStatus: MedEntitlementsLoadStatus;
};

const initialState: MedEntitlementsState = { loadStatus: "idle" };

export const loadMedEntitlements = createAppAsyncThunk<void, MedEntitlements>(
  "medEntitlements/loadMedEntitlements",
  async () => {
    const URL = "/api/usermodel/v1/getMedEntitlements/";

    try {
      return await send<MedEntitlements>("GET", URL);
    } catch (e) {
      captureException(e, "loadMedEntitlements");
      throw e;
    }
  }
);

const medEntitlementsSlice = createSlice({
  name: "medEntitlements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadMedEntitlements.pending, (state) => ({
        ...state,
        loadStatus: "loading",
      }))
      .addCase(loadMedEntitlements.fulfilled, (_state, action) => ({
        ...action.payload,
        loadStatus: "success",
      }))
      // On failure we drop any previously loaded entitlement fields so
      // consumers that don't gate on `loadStatus` (e.g. legacy callers of
      // `isMedicationCompanionUser()`) don't read stale values after a refresh
      // that failed. Treat failure as "unknown".
      .addCase(loadMedEntitlements.rejected, () => ({
        loadStatus: "failure",
      }));
  },
});

export default medEntitlementsSlice;
